/**
 * Login Page Component
 */

'use client';

import styled from '@emotion/styled';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '@/lib/services/auth.service';
import { useGoogleLogin } from '@/lib/hooks/use-google-login';
import { storefrontLayoutLogger } from '@/lib/logger';

const PageContainer = styled.div`
  display: flex;
  min-height: calc(100vh - 200px);
  
  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    flex-direction: column;
  }
`;

const ImageSection = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #f8d7d3 0%, #f5c6c0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  position: relative;
  overflow: hidden;
  
  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    min-height: 400px;
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.space['3xl']};
  background: ${({ theme }) => theme.colors.background.secondary};
  
  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    padding: ${({ theme }) => theme.space.xl};
  }
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 550px;
  background: ${({ theme }) => theme.colors.background.primary};
  padding: ${({ theme }) => theme.space['3xl']};
  border-radius: ${({ theme }) => theme.radius.regular};
  margin-top: ${({ theme }) => theme.space['2xl']};
  
  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    padding: ${({ theme }) => theme.space.xl};
  }
`;

const Title = styled.h1`
  margin-bottom: ${({ theme }) => theme.space['2xl']};
  text-align: left;
  font-size: ${({ theme }) => theme.typography.variants.heading3.fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: ${({ theme }) => theme.typography.variants.heading3.lineHeight};
  margin-top: 0;
`;

const InputGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.space.xl};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.typography.variants['body-regular'].fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: inherit;
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.lg};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.typography.variants['body-regular'].fontSize}px;
  font-family: inherit;
  transition: all 0.2s;
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.text.primary};
    box-shadow: none;
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`;

const ContinueButton = styled.button`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.background.primary};
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.xl};
  font-size: ${({ theme }) => theme.typography.variants['body-regular'].fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  border: none;
  border-radius: ${({ theme }) => theme.radius.small};
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  letter-spacing: 0.5px;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.variants['body-small'].fontSize}px;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin-top: ${({ theme }) => theme.space.md};
  font-family: inherit;
  
  &:hover {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const SignUpText = styled.div`
  margin-top: ${({ theme }) => theme.space.lg};
  margin-bottom: ${({ theme }) => theme.space.xl};
  font-size: ${({ theme }) => theme.typography.variants['body-small'].fontSize}px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: inherit;
`;

const SignUpLink = styled(Link)`
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: underline;
  cursor: pointer;
  
  &:hover {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  margin: ${({ theme }) => theme.space['2xl']} 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
  }
  
  &::before {
    margin-right: ${({ theme }) => theme.space.lg};
  }
  
  &::after {
    margin-left: ${({ theme }) => theme.space.lg};
  }
`;

const DividerText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.variants['body-small'].fontSize}px;
  font-family: inherit;
`;

const GoogleButtonContainer = styled.div`
  width: 100%;
  display: none; /* Hidden until Google is configured */
  
  /* Google button will be rendered inside this container */
  & > div {
    width: 100% !important;
  }
  
  /* Override Google's default styles to match our design */
  & iframe {
    width: 100% !important;
  }
  
  /* Show when Google button is rendered */
  &:has(div) {
    display: block;
  }
`;

const GoogleButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.space.sm};
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.xl};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.typography.variants['body-regular'].fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  
  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    border-color: ${({ theme }) => theme.colors.text.secondary};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.space.md};
  background: ${({ theme }) => theme.colors.semantic.urgent.background};
  color: ${({ theme }) => theme.colors.semantic.urgent.color};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.typography.variants['body-small'].fontSize}px;
  font-family: inherit;
  margin-bottom: ${({ theme }) => theme.space.lg};
  text-align: center;
`;

const BenefitsSection = styled.div`
  width: 100%;
  max-width: 550px;
  margin-top: ${({ theme }) => theme.space['2xl']};
  padding-top: ${({ theme }) => theme.space['2xl']};
`;

const BenefitsTitle = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.space['2xl']};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.variants['body-regular'].fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  font-family: inherit;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.space.lg};
  
  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const BenefitItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${({ theme }) => theme.space.sm};
`;

const BenefitIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.colors.semantic.urgent.background};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.semantic.urgent.color};
  font-size: 24px;
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

const BenefitText = styled.div`
  font-size: ${({ theme }) => theme.typography.variants['body-small'].fontSize}px;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.4;
  font-family: inherit;
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
`;

const ScrollToTopButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.space.sm};
  margin: ${({ theme }) => theme.space['2xl']} auto ${({ theme }) => theme.space.xl};
  padding: 0;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.variants['body-small'].fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  cursor: pointer;
  transition: color 0.2s;
  font-family: inherit;
  letter-spacing: 0.5px;
  
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const ScrollIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.colors.semantic.urgent.background};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.semantic.urgent.color};
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
`;

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Session is automatically stored by authService.login()
      // Access token is in memory, refresh token is in HTTP-only cookie
      const response = await authService.login({ email, password });
      
      // Redirect to home page with replace to clear login from history
      navigate('/', { replace: true });
      
      storefrontLayoutLogger.info('Login successful', {
        hasDisplayName: !!response.user.displayName,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      storefrontLayoutLogger.error('Login failed', err instanceof Error ? err : undefined, {
        email,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credential: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // Session is automatically stored by authService.loginWithGoogle()
      // Access token is in memory, refresh token is in HTTP-only cookie
      const response = await authService.loginWithGoogle({ idToken: credential });
      
      // Redirect to home page with replace to clear login from history
      navigate('/', { replace: true });
      
      storefrontLayoutLogger.info('Google login successful', {
        hasDisplayName: !!response.user.displayName,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Google login failed';
      setError(errorMessage);
      storefrontLayoutLogger.error('Google login failed', err instanceof Error ? err : undefined);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = (err: Error) => {
    setError(err.message);
    storefrontLayoutLogger.error('Google login error callback', err);
  };

  const { buttonRef } = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
  });

  const handleCustomGoogleClick = () => {
    // Fallback when Google isn't configured
    setError('Google Sign-In is not configured. Please set VITE_GOOGLE_CLIENT_ID environment variable.');
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <PageContainer>
      {/* Left: Image Section */}
      <ImageSection>
        <ProductImage
          src="https://cdn.shapermint.com/assets/shapermint/images/SHM_Account_Login-img.jpg"
          alt="Shapermint Models"
          loading="lazy"
        />
      </ImageSection>

      {/* Right: Login Form */}
      <FormSection>
        <FormContainer>
          <Title>
            Log In to Your Account
          </Title>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <form onSubmit={handleContinue}>
            <InputGroup>
              <Label htmlFor="email">Email:</Label>
              <Input
                id="email"
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="password">Password:</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </InputGroup>

            <ContinueButton type="submit" disabled={isLoading}>
              {isLoading ? 'LOADING...' : 'CONTINUE'}
            </ContinueButton>
          </form>

          <SignUpText>
            Don't have an account yet?{' '}
            <SignUpLink to="/register">Sign up here.</SignUpLink>
          </SignUpText>

          <Divider>
            <DividerText>or</DividerText>
          </Divider>

          {/* Google Identity Services button (renders automatically if configured) */}
          <GoogleButtonContainer ref={buttonRef} />
          
          {/* Fallback button (shows if Google not configured) */}
          <GoogleButton 
            onClick={handleCustomGoogleClick} 
            type="button"
            disabled={isLoading}
          >
            <svg width="20" height="20" viewBox="0 0 20 20">
              <path
                fill="#4285F4"
                d="M19.6 10.23c0-.82-.1-1.42-.25-2.05H10v3.72h5.5c-.15.96-.74 2.31-2.04 3.22v2.45h3.16c1.89-1.73 2.98-4.3 2.98-7.34z"
              />
              <path
                fill="#34A853"
                d="M13.46 15.13c-.83.59-1.96 1-3.46 1-2.64 0-4.88-1.74-5.68-4.15H1.07v2.52C2.72 17.75 6.09 20 10 20c2.7 0 4.96-.89 6.62-2.42l-3.16-2.45z"
              />
              <path
                fill="#FBBC05"
                d="M3.99 10c0-.69.12-1.35.32-1.97V5.51H1.07A9.973 9.973 0 000 10c0 1.61.39 3.14 1.07 4.49l3.24-2.52c-.2-.62-.32-1.28-.32-1.97z"
              />
              <path
                fill="#EA4335"
                d="M10 3.88c1.88 0 3.13.81 3.85 1.48l2.84-2.76C14.96.99 12.7 0 10 0 6.09 0 2.72 2.25 1.07 5.51l3.24 2.52C5.12 5.62 7.36 3.88 10 3.88z"
              />
            </svg>
            Sign in with Google
          </GoogleButton>

          <BenefitsSection>
            <BenefitsTitle>
              Sign up to access exclusive features
            </BenefitsTitle>

            <BenefitsGrid>
              <BenefitItem>
                <BenefitIcon>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z"/>
                  </svg>
                </BenefitIcon>
                <BenefitText>
                  Manage your<br/>subscriptions
                </BenefitText>
              </BenefitItem>

              <BenefitItem>
                <BenefitIcon>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                  </svg>
                </BenefitIcon>
                <BenefitText>
                  Exchanges &<br/>Returns
                </BenefitText>
              </BenefitItem>

              <BenefitItem>
                <BenefitIcon>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                </BenefitIcon>
                <BenefitText>
                  Track your<br/>order
                </BenefitText>
              </BenefitItem>

              <BenefitItem>
                <BenefitIcon>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                  </svg>
                </BenefitIcon>
                <BenefitText>
                  Access limited-<br/>time offers
                </BenefitText>
              </BenefitItem>
            </BenefitsGrid>

            <ScrollToTopButton onClick={scrollToTop}>
              SCROLL TO TOP
              <ScrollIcon>↑</ScrollIcon>
            </ScrollToTopButton>
          </BenefitsSection>
        </FormContainer>
      </FormSection>
    </PageContainer>
  );
}
